import { Category, ICategoryDataV2 } from '@df/catalog/category/category';
import { IPage, IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { Model } from '@df/catalog/model';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Search } from '@df/search/search.interfaces';
import each from 'lodash-es/each';
import { NgInject } from '../../dragonfly-ng/src/ng-inject';

export class SearchResult {
    @NgInject(App)
    protected app!: App;

    /**
     * Model constructor
     */
    constructor(
        public term: string,
        public data: Search.IResult,
        protected options: Search.ISearchResultOptions
    ) {}

    /**
     * Checks if there are any results loaded
     */
    hasAny(): boolean {
        return !!(this.products || this.pages || this.categories);
    }

    /**
     * Test for result including products
     */
    get hasProducts(): boolean {
        const products = this.products;
        return products ? products.length > 0 : false;
    }

    /**
     * Loads dependencies
     */
    load(): Promise<Model[][]> {
        const promises: Promise<Model[]>[] = [];

        if (this.data) {
            each(this.data.result, (ids, type) => {
                if (ids && ids.length) {
                    const resource = this.app.resources[type];

                    if (resource) {
                        const promise = resource.loadAllIds(ids, 2, { safe: true });
                        if (promise) {
                            promises.push(promise);
                        }
                    }
                }
            });
        }

        return Promise.all(promises);
    }

    /**
     * Universal models getter
     */
    getModels<T extends Model = Model>(type: string): T[] | undefined {
        const resource = this.app.resources[type];

        if (resource && this.data) {
            const models = resource.getAllExisting(this.data.result[type] || []);

            if (models.length) {
                if (type === 'product') {
                    return this.applyProductFilter(models as any as Product[]) as any as T[];
                }

                return models as T[];
            }
        }

        return undefined;
    }

    protected applyProductFilter(models: Product[]): Product[] {
        if (this.options.stockCheck) {
            return models.filter(i => !i.isOutOfStock);
        }

        return models;
    }

    /**
     * Products getter
     */
    get products(): Product<IProductDataV2>[] | undefined {
        return this.getModels<Product<IProductDataV2>>('product');
    }

    /**
     * Categories getter
     */
    get categories(): Category<ICategoryDataV2>[] | undefined {
        return this.getModels<Category<ICategoryDataV2>>('category');
    }

    /**
     * Pages getter
     */
    get pages(): IPage<IPageDataV2>[] | undefined {
        return this.getModels('page');
    }

    /**
     * Terms getter
     */
    get terms(): Search.ITerm[] | undefined {
        return this.data && this.data.terms && this.data.terms.length ? this.data.terms : undefined;
    }
}
